import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { EMPTY, interval, startWith, switchMap } from 'rxjs';
import { PartnerIncomeTaxStateService } from './partner-income-tax-state.service';

/** De quanto em quanto tempo o "há 30 s" da leitura avança na tela. */
const TICK_MS = 5000;

/**
 * Relógio do andamento da leitura (card 3772): só faz o "Começou há 1 min" avançar na tela. Não chama a API; quem traz
 * o estado novo é o tempo real (evento `partner-income-tax.changed`). Só anda enquanto há documento em leitura: com a
 * aba parada, nenhum timer dispara a detecção de mudanças. Provido pela aba IR dos sócios, para a aba e a lista de
 * documentos lerem o mesmo instante.
 */
@Injectable()
export class PartnerIncomeTaxReadingClock {
  private readonly dates = inject(DateFormatterService);
  private readonly state = inject(PartnerIncomeTaxStateService);
  private readonly nowMs = signal(this.currentMs());

  /** Há leitura em andamento (rodada aberta ou documento em leitura na visão geral). */
  private readonly active = computed(() => {
    const overview = this.state.overview();
    return (
      !!overview?.readingProgress ||
      !!overview?.processing.some(item => item.status === 'processing')
    );
  });

  /** Instante atual, em ms, atualizado a cada {@link TICK_MS} enquanto há leitura em andamento. */
  readonly now = this.nowMs.asReadonly();

  constructor() {
    toObservable(this.active)
      .pipe(
        switchMap(active => (active ? interval(TICK_MS).pipe(startWith(0)) : EMPTY)),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe(() => this.nowMs.set(this.currentMs()));
  }

  /** Segundos desde `iso` (UTC do backend) até agora; `null` sem data. */
  elapsedSeconds(iso: string | null | undefined): number | null {
    const from = this.dates.parseDate(iso);
    if (!from) return null;
    return Math.max(0, (this.now() - from.getTime()) / 1000);
  }

  /** Instante atual pelo `DateFormatterService` (régua de datas do projeto). */
  private currentMs(): number {
    return this.dates.getNow().getTime();
  }
}
